import React, {useCallback} from 'react';
import {GatewayGetSchema} from '../../../../../api/DBSchema/Gateway';
import {SMCCheckbox} from '../../../../../components/Simple/SMCCheckbox';
import {useTranslation} from 'react-i18next';
import PlanEditorInnerContainer from '../../../../../components/Editor/PlanEditorInnerContainer';
import Separator from '../../../../../components/Simple/Sepperator';
import Indentation from '../../../../../components/Simple/Indentation';
import {SMCRadio, SMCRadioGroup} from '../../../../../components/Simple/SMCRadio';
import SMCLabel from '../../../../../components/Simple/SMCLabel';
import EmptyRow from '../../../../../components/Simple/EmptyRow';
import SMCNote from '../../../../../components/Simple/SMCNote';
import EditorLayout from '../../../../../components/Layouts/EditorLayout';
import RowLayout from '../../../../../components/Layouts/RowLayout';
import SMCLink from '../../../../../components/Simple/SMCLink';
import AppiEditorModal from '../../../../PopupPages/AppiEditorModal';
import {useBoolean} from '../../../../../utils/hooks';
import {SMCToggleSwitch} from "../../../../../components/Simple/SMCToggleSwitch";

const pageSchema = GatewayGetSchema.pages.applicationsAndUrls;
const pageId = pageSchema.pageId;

function BaseApplicationsAndUrls({
                                     pageData,
                                     setObjectData,
                                     pageDisabled,
                                     setAppiDataValue,
                                 }) {
    const {t} = useTranslation();

    const setPageValue = useCallback((param, newValue) => {
        setObjectData([pageId, param], newValue);
    }, [setObjectData]);

    const manageInSmp = pageData[pageSchema.manageInSmp];
    const mode = pageData[pageSchema.mode];
    const blockSecurityRiskCategories = pageData[pageSchema.blockSecurityRiskCategories];
    const blockFileSharingApplications = pageData[pageSchema.blockFileSharingApplications];
    const blockInappropriateContent = pageData[pageSchema.blockInappropriateContent];
    const blockOtherUndesiredApplications = pageData[pageSchema.blockOtherUndesiredApplications];
    const appiBlockList = pageData[pageSchema.appiBlockList];

    const [isAppiEditorOpen, openAppiEditor, closeAppiEditor] = useBoolean(false);

    return (
        <EditorLayout>
            <SMCToggleSwitch
                disabled={pageDisabled}
                checked={manageInSmp}
                onChange={checked => setPageValue(pageSchema.manageInSmp, checked)}
                label={t('SMP.PLAN.MANAGE_IN_SMP')}
            />
            <Separator isMainSeparator/>
            <SMCNote>
                {t('SMP.PLAN.APP_URLS.SEE')}&nbsp;
                <SMCLink inline
                         href="https://supportcenter.checkpoint.com/supportcenter/portal?eventSubmit_doGoviewsolutiondetails=&solutionid=sk135433"
                         openInNewTab>sk135433</SMCLink>&nbsp;
                {t('SMP.PLAN.APP_URLS.SEE.LINE1')}
            </SMCNote>
            <PlanEditorInnerContainer title={t('SMP.PLAN.NEW_PLAN_SB.APP_FILTER')} managedInSmp={manageInSmp}>
                <RowLayout>
                    <SMCRadioGroup value={mode ? 'ON' : 'OFF'}
                                   onChange={e => setPageValue(pageSchema.mode, e.target.value === 'ON')}
                                   disabled={pageDisabled || !manageInSmp}>
                        <SMCRadio value="ON" label={t('SMP.ON')}/>
                        <SMCRadio value="OFF" label={t('SMP.OFF')}/>
                    </SMCRadioGroup>
                </RowLayout>
                <SMCLabel bold>{t('SMP.PLAN.SEC_SOFT_BLADES.POLICY')}:</SMCLabel>
                {/* Block security risk categories */}
                <SMCCheckbox
                    disabled={pageDisabled || !manageInSmp}
                    checked={blockSecurityRiskCategories}
                    onChange={e => setPageValue(pageSchema.blockSecurityRiskCategories, e.target.checked)}
                    label={t('SMP.PLAN.APP_URLS.LINE2')}
                    tooltip={t('SMP.PLAN.APP_URLS.LINE2.TOOLTIP')}
                />
                {/* Block fie sharing applications */}
                <SMCCheckbox
                    disabled={pageDisabled || !manageInSmp}
                    checked={blockFileSharingApplications}
                    onChange={e => setPageValue(pageSchema.blockFileSharingApplications, e.target.checked)}
                    label={t('SMP.PLAN.APP_URLS.LINE3')}
                    tooltip={t('SMP.PLAN.APP_URLS.LINE3.TOOLTIP')}
                />
                {/* Block inappropriate content */}
                <SMCCheckbox
                    disabled={pageDisabled || !manageInSmp}
                    checked={blockInappropriateContent}
                    onChange={e => setPageValue(pageSchema.blockInappropriateContent, e.target.checked)}
                    label={t('SMP.PLAN.APP_URLS.LINE4')}
                    tooltip={t('SMP.PLAN.APP_URLS.LINE4.TOOLTIP')}
                />
                {/* Block undesired applications */}
                <RowLayout spacing="4px">
                    <SMCCheckbox
                        disabled={pageDisabled || !manageInSmp}
                        checked={blockOtherUndesiredApplications}
                        onChange={e => setPageValue(pageSchema.blockOtherUndesiredApplications, e.target.checked)}
                        label={t('SMP.PLAN.APP_URLS.BLOCK')}
                        inline
                    />
                    <SMCLink
                        disabled={pageDisabled || !manageInSmp}
                        onClick={openAppiEditor}
                        inline
                    >
                        {t('SMP.PLAN.APP_URLS.BLOCK.LINE1')}
                    </SMCLink>
                </RowLayout>
                <SMCLabel bold>{t('SMP.PLAN.APP_URLS.NOTES')}</SMCLabel>
                <Indentation>
                    <SMCLabel>{t('SMP.PLAN.APP_URLS.NOTES.LINE1')}</SMCLabel>
                    <RowLayout>
                        2. {t('SMP.PLAN.APP_URLS.SEE')}&nbsp;
                        <SMCLink
                            href="https://supportcenter.checkpoint.com/supportcenter/portal?eventSubmit_doGoviewsolutiondetails=&solutionid=sk135433"
                            openInNewTab>sk135433</SMCLink>&nbsp;
                        {t('SMP.PLAN.APP_URLS.SEE.LINE1')}
                    </RowLayout>
                </Indentation>

                <EmptyRow/>

                <SMCLabel><b>{t('SMP.DESCRIPTION')}: </b>{t('SMP.PLAN.APP_URLS.LINE5')}</SMCLabel>
            </PlanEditorInnerContainer>
            {/* Floating windowns */}
            {isAppiEditorOpen && <AppiEditorModal
                isOpen={isAppiEditorOpen}
                onRequestClose={closeAppiEditor}
                deafultSelected={appiBlockList ? appiBlockList.split(',') : []}
                onSuccess={newAppiBlockList => {
                    setPageValue(pageSchema.appiBlockList, newAppiBlockList.join(','));
                    closeAppiEditor();
                }}
                appType="APPI"
                setAppiDataValue={setAppiDataValue}
            />}

        </EditorLayout>
    );
}

export default BaseApplicationsAndUrls;